import React, { useState, useEffect } from "react";
import {
  Clock,
  Play,
  Loader2,
  ArrowLeft,
  X,
  Square,
  Check,
  Pencil,
  FileText,
} from "lucide-react";
import { useRunningTimer } from "../../contexts/RunningTimerContext";
import { invoicesApi, taxSettingsApi } from "../../services/api";
import {
  TaxConfig,
  DEFAULT_TAX_CONFIG,
  calculateTaxCents,
  getTaxLabel,
} from "../../utils/taxCalculation";

interface TimerSubPanelProps {
  matterId?: string;
  clientId?: string;
  matterTitle: string;
  matterNumber?: string;
  onStarted?: () => void;
  onClose: () => void;
}

/** Format seconds to HH:MM:SS */
const formatElapsed = (totalSec: number) => {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

const formatCurrency = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`;

const TimerSubPanel: React.FC<TimerSubPanelProps> = ({
  matterId,
  clientId,
  matterTitle,
  matterNumber,
  onStarted,
  onClose,
}) => {
  const {
    runningTimer,
    stoppedEntry,
    elapsedSeconds,
    startTimer,
    stopTimer,
    updateStoppedEntry,
    dismissStoppedEntry,
    loading: timerLoading,
  } = useRunningTimer();

  const [narrative, setNarrative] = useState("");
  const [rateDollars, setRateDollars] = useState("250.00");
  const [billable, setBillable] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");

  // ── Stopped entry review state ──────────────────────────────────
  const [editMinutes, setEditMinutes] = useState(0);
  const [editNarrative, setEditNarrative] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // ── Invoice generation state ────────────────────────────────────
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [invoiceSent, setInvoiceSent] = useState(false);
  const [taxConfig, setTaxConfig] = useState<TaxConfig>(DEFAULT_TAX_CONFIG);

  const isTimerRunning = runningTimer?.timer?.isRunning;

  // Sync edit fields when stoppedEntry arrives
  useEffect(() => {
    if (stoppedEntry) {
      setEditMinutes(stoppedEntry.minutes || 1);
      setEditNarrative(stoppedEntry.narrative || "");
      setSaved(false);
      setInvoiceSent(false);
    }
  }, [stoppedEntry]);

  // Load tax config for invoice generation
  useEffect(() => {
    taxSettingsApi
      .get()
      .then((res) => {
        const d = res.data?.data || res.data;
        if (d?.taxMode) {
          setTaxConfig({
            taxMode: d.taxMode,
            hstRate: d.hstRate ?? 13,
            gstRate: d.gstRate ?? 5,
            pstRate: d.pstRate ?? 0,
          });
        }
      })
      .catch(() => {});
  }, []);

  const handleStart = async () => {
    setStarting(true);
    setError("");
    try {
      await startTimer({
        matterId,
        clientId,
        narrative: narrative.trim() || "Timer in progress",
        rateCents: Math.round(parseFloat(rateDollars || "0") * 100),
        billable,
      });
      onStarted?.();
    } catch (err: any) {
      const data = err?.response?.data;
      const msg =
        data?.error || data?.msg || data?.message || "Failed to start timer";
      setError(
        msg.includes("already")
          ? "A timer is already running — stop it first"
          : msg
      );
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async () => {
    try {
      await stopTimer();
    } catch {
      setError("Failed to stop timer");
    }
  };

  const handleSaveEdits = async () => {
    if (!stoppedEntry) return;
    setSaving(true);
    try {
      await updateStoppedEntry(stoppedEntry._id, {
        minutes: editMinutes,
        narrative: editNarrative.trim(),
      });
      setSaved(true);
      setTimeout(() => dismissStoppedEntry(), 1500);
    } catch {
      setError("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (!stoppedEntry) return;
    setGeneratingInvoice(true);
    setError("");
    try {
      // First save the edits
      await updateStoppedEntry(stoppedEntry._id, {
        minutes: editMinutes,
        narrative: editNarrative.trim(),
      });

      // Calculate amounts
      const amountCents = Math.round(
        (editMinutes / 60) * (stoppedEntry.rateCents || 0)
      );
      const { totalTaxCents } = stoppedEntry.billable
        ? calculateTaxCents(amountCents, taxConfig)
        : { totalTaxCents: 0 };

      // Determine matter/client IDs
      const entryMatterId =
        stoppedEntry.matterId && typeof stoppedEntry.matterId === "object"
          ? stoppedEntry.matterId._id
          : stoppedEntry.matterId;
      const entryClientId =
        stoppedEntry.clientId && typeof stoppedEntry.clientId === "object"
          ? stoppedEntry.clientId._id
          : stoppedEntry.clientId;

      // Create + send invoice
      const unitPriceCents = stoppedEntry.rateCents || 0;
      const res = await invoicesApi.create({
        matterId: entryMatterId,
        clientId: entryClientId,
        lineItems: [
          {
            kind: "fee",
            description: editNarrative.trim() || "Legal services",
            quantity: parseFloat((editMinutes / 60).toFixed(2)),
            unitPriceCents,
            amountCents,
            taxable: stoppedEntry.billable,
            taxAmountCents: totalTaxCents,
          },
        ],
        dueAt: new Date(Date.now() + 30 * 86400000)
          .toISOString()
          .split("T")[0],
      });

      const invoiceId = res.data?._id;
      if (invoiceId) {
        try {
          await invoicesApi.send(invoiceId);
        } catch {
          // Created but send failed — still success
        }
      }

      setInvoiceSent(true);
      setTimeout(() => dismissStoppedEntry(), 2000);
    } catch (err: any) {
      const data = err?.response?.data;
      setError(
        data?.error ||
          data?.msg ||
          data?.message ||
          "Failed to generate invoice"
      );
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Derive running timer info
  const runningMatterTitle =
    runningTimer && runningTimer.matterId && typeof runningTimer.matterId === "object"
      ? runningTimer.matterId.title
      : "Matter";
  const runningMatterNumber =
    runningTimer && runningTimer.matterId && typeof runningTimer.matterId === "object"
      ? runningTimer.matterId.matterNumber
      : undefined;

  // Stopped entry calculated amount
  const stoppedAmountCents = stoppedEntry
    ? Math.round((editMinutes / 60) * (stoppedEntry.rateCents || 0))
    : 0;

  // Show review when we have a stoppedEntry and no running timer
  const showReview = !!stoppedEntry && !isTimerRunning;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white shrink-0">
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 transition-colors md:hidden"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div
          className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
            showReview ? "bg-slate-100" : "bg-emerald-50"
          }`}
        >
          <Clock
            className={`h-4 w-4 ${
              showReview ? "text-slate-600" : "text-emerald-600"
            }`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-slate-800">
            {showReview
              ? "Timer Stopped — Review"
              : isTimerRunning
              ? "Timer Running"
              : "Start Timer"}
          </h3>
          <p className="text-xs text-slate-500 truncate">
            {matterNumber ? `${matterNumber} — ${matterTitle}` : matterTitle}
          </p>
        </div>
        <button
          onClick={showReview ? dismissStoppedEntry : onClose}
          className="text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
        {/* ── RUNNING TIMER BANNER ─────────────────────────── */}
        {isTimerRunning && (
          <div
            className="rounded-xl p-4 text-center"
            style={{
              backgroundColor:
                "color-mix(in srgb, var(--color-primary) 8%, transparent)",
            }}
          >
            <p
              className="text-3xl font-mono font-bold tracking-wider"
              style={{ color: "var(--color-primary)" }}
            >
              {formatElapsed(elapsedSeconds)}
            </p>
            <p className="text-sm text-slate-600 mt-1 truncate">
              {runningTimer?.narrative || "Timer in progress"}
            </p>
            <p className="text-xs text-slate-400 mt-0.5 truncate">
              {runningMatterNumber
                ? `${runningMatterNumber} — ${runningMatterTitle}`
                : runningMatterTitle}
            </p>
            <button
              onClick={handleStop}
              disabled={timerLoading}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
            >
              {timerLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Square className="h-3.5 w-3.5 fill-current" />
              )}
              Stop Timer
            </button>
          </div>
        )}

        {/* ── STOPPED ENTRY REVIEW ─────────────────────────── */}
        {showReview && (
          <>
            {/* Time + Amount summary */}
            <div className="rounded-xl bg-slate-50 p-4 text-center">
              <p className="text-2xl font-mono font-bold text-slate-800 tracking-wider">
                {editMinutes} min
              </p>
              <p className="text-lg font-bold mt-1" style={{ color: "var(--color-primary)" }}>
                {formatCurrency(stoppedAmountCents)}
              </p>
              {stoppedEntry.rateCents > 0 && (
                <p className="text-xs text-slate-400 mt-1">
                  ${(stoppedEntry.rateCents / 100).toFixed(2)}/hr
                  {stoppedEntry.billable ? " · Billable" : " · Non-billable"}
                </p>
              )}
            </div>

            {/* Editable narrative */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={editNarrative}
                onChange={(e) => setEditNarrative(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>

            {/* Editable minutes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Time (minutes)
              </label>
              <input
                type="number"
                min="1"
                value={editMinutes}
                onChange={(e) =>
                  setEditMinutes(Math.max(1, parseInt(e.target.value) || 1))
                }
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
          </>
        )}

        {/* ── START TIMER FORM ─────────────────────────────── */}
        {!isTimerRunning && !showReview && (
          <>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                What are you working on?
              </label>
              <input
                type="text"
                value={narrative}
                onChange={(e) => setNarrative(e.target.value)}
                placeholder="e.g. Reviewing documents, Client call..."
                autoFocus
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Rate ($/hr)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                    $
                  </span>
                  <input
                    type="number"
                    value={rateDollars}
                    onChange={(e) => setRateDollars(e.target.value)}
                    step="0.01"
                    min="0"
                    className="w-full rounded-xl border border-slate-200 pl-8 pr-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                  />
                </div>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={billable}
                    onChange={(e) => setBillable(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Billable
                  </span>
                </label>
              </div>
            </div>
          </>
        )}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
            {error}
          </p>
        )}
      </div>

      {/* ── FOOTER ───────────────────────────────────────── */}
      {showReview && (
        <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/50 shrink-0 space-y-2">
          {/* Save + Dismiss row */}
          <div className="flex gap-2">
            <button
              onClick={dismissStoppedEntry}
              className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Dismiss
            </button>
            <button
              onClick={handleSaveEdits}
              disabled={saving || saved}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : saved ? (
                <Check className="h-4 w-4" />
              ) : (
                <Pencil className="h-4 w-4" />
              )}
              {saving ? "Saving..." : saved ? "Saved!" : "Save Entry"}
            </button>
          </div>
          {/* Generate Invoice button */}
          {stoppedEntry?.billable && (
            <button
              onClick={handleGenerateInvoice}
              disabled={generatingInvoice || invoiceSent}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-bold disabled:opacity-50 transition-colors"
              style={{
                borderColor: "var(--color-primary)",
                color: invoiceSent ? "white" : "var(--color-primary)",
                backgroundColor: invoiceSent ? "var(--color-primary)" : "transparent",
              }}
            >
              {generatingInvoice ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : invoiceSent ? (
                <Check className="h-4 w-4" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              {generatingInvoice
                ? "Creating Invoice..."
                : invoiceSent
                ? "Invoice Sent!"
                : `Invoice ${formatCurrency(stoppedAmountCents)}`}
            </button>
          )}
        </div>
      )}

      {!isTimerRunning && !showReview && (
        <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/50 shrink-0 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleStart}
            disabled={starting}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => {
              if (!starting)
                e.currentTarget.style.backgroundColor =
                  "var(--color-primary-hover)";
            }}
            onMouseLeave={(e) => {
              if (!starting)
                e.currentTarget.style.backgroundColor = "var(--color-primary)";
            }}
          >
            {starting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {starting ? "Starting..." : "Start Timer"}
          </button>
        </div>
      )}
    </div>
  );
};

export default TimerSubPanel;
